import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { defaultLocale, isLocale, isRtl, locales, type Locale } from "./config";
import type { Dictionary, TranslationKey } from "./dictionaries/en";
import { CURRENCIES, formatMoney, formatMoneyRange } from "@shared/currency";
import type { CurrencyCode } from "@shared/types";

/**
 * i18n + 幣別的單一 Provider
 * ---------------------------------------------------------------
 * 語系來源優先序：URL 的 /:lang 區段 → localStorage → 瀏覽器語言 → en
 * 幣別來源優先序：localStorage → 由語系推測 → USD
 *
 * 字典查不到 key 時會 fallback 到英文，再 fallback 到 key 本身，
 * 所以新語言可以只翻一半就先上線。
 */

/**
 * 字典的載入器
 * ═══════════════════════════════════════════════════════════════
 *
 * ── 為什麼是動態 import，不是直接 import ────────────────────
 *
 * 直接 import 的話，Vite 會把**十八本字典全部**打進主 bundle。
 * 實測 2.75 MB，其中 2.6 MB 是翻譯 —— 每一個訪客下載十八種語言，
 * 只為了讀其中一種。
 *
 * 語系少的時候這個代價看不出來（八本約 1.2 MB，慢但還能忍），
 * 十八本就變成第一次載入要多花好幾秒。而那是所有人都要付的，
 * 包括只會看英文的那些人。
 *
 * 動態 import 讓 Vite 把每一本切成獨立的 chunk，需要的時候才抓。
 *
 * ── en 為什麼留在主 bundle ──────────────────────────────────
 *
 * 它是**退路**：t() 在目標語系查不到 key 時會退回英文，
 * 而那個退路必須是同步可用的 —— 一個要等網路的退路不是退路。
 * 它也是最多人用的那一本。
 */
const LOADERS: Record<Locale, () => Promise<{ default: Partial<Dictionary> }>> = {
  /*
    ── 英文也是動態載入的 ────────────────────────────────────

    它原本是 `import en from "./dictionaries/en"` —— 一個靜態的
    import，所以整份英文字典（約 190 KB 原始、60 KB 傳輸）被打包
    進主 bundle，而主 bundle 是**每一個使用者**都會下載的東西。

    一個台灣使用者因此下載了兩份字典：主 bundle 裡的英文，
    以及他真正要用的中文。

    英文留在主 bundle 的唯一理由是「別的語系載入失敗時的退路」。
    那個理由是真的，但它不需要預先下載 —— 失敗的時候再抓就好，
    而那件事幾乎不會發生（同源、immutable、CDN）。
  */
  "en": () => import("./dictionaries/en"),
  "zh-TW": () => import("./dictionaries/zh-TW"),
  "ja": () => import("./dictionaries/ja"),
  "ko": () => import("./dictionaries/ko"),
  "es": () => import("./dictionaries/es"),
  "fr": () => import("./dictionaries/fr"),
  "de": () => import("./dictionaries/de"),
  "pt-BR": () => import("./dictionaries/pt-BR"),
  "it": () => import("./dictionaries/it"),
  "nl": () => import("./dictionaries/nl"),
  "pl": () => import("./dictionaries/pl"),
  "tr": () => import("./dictionaries/tr"),
  "id": () => import("./dictionaries/id"),
  "th": () => import("./dictionaries/th"),
  "vi": () => import("./dictionaries/vi"),
  "ru": () => import("./dictionaries/ru"),
  "ar": () => import("./dictionaries/ar"),
  "hi": () => import("./dictionaries/hi"),
};

/*
  已經載好的字典。

  模組層級的 Map 而不是元件的 state —— 換語言再換回來時不該
  重新下載，而且同一個 chunk 瀏覽器本來就有快取，
  這一層只是省掉重新解析與 Promise 的往返。
*/
const LOADED = new Map<Locale, Partial<Dictionary>>();

/**
 * 在 React 掛載**之前**把字典載好。
 * ═══════════════════════════════════════════════════════════════
 *
 * ── 為什麼需要這一步 ────────────────────────────────────────
 *
 * 字典改成動態 import 之後，Provider 的初始值只能是英文（那是
 * 唯一同步拿得到的一本）。結果是每一個非英語使用者的第一次
 * 繪製都是**英文內容**，然後才換成他的語言。
 *
 * 那個瞬間看起來不像「正在載入」，看起來像網站壞了 ——
 * 而且語言選單顯示的是「繁體中文」，畫面上卻是英文，
 * 兩者對不起來。使用者的結論會是「切換反了」。
 *
 * 對右至左的語言更糟：版面會先向左排好再整個翻過來。
 *
 * ── 為什麼在這裡做，不是在 Provider 裡擋住畫面 ──────────────
 *
 * Provider 擋住畫面的話，非英語使用者會先看到一片空白 ——
 * 用空白換掉英文閃爍，只是換一種壞法。
 *
 * 在 main.tsx 掛載前先載，那段等待和 React 自己的初始化重疊，
 * 使用者看到的仍然是 Worker 注入的 SEO 骨架（有標題、有描述），
 * 而不是空白或錯的語言。
 *
 * 拿不到就算了：t() 本來就會在查不到 key 時退回英文，
 * 而英文介面比一個當掉的畫面好。
 */
export async function preloadDictionary(locale: string): Promise<void> {
  if (LOADED.has(locale as Locale)) return;

  const load = LOADERS[locale as Locale];
  if (!load) return;

  try {
    const mod = await load();
    LOADED.set(locale as Locale, mod.default);
  } catch {
    /*
      ── 載不到就抓英文 ────────────────────────────────────────

      英文以前是靜態打包在主 bundle 裡的，所以這裡什麼都不必做。
      現在它也是動態的，退路要自己抓一次。

      這條路徑幾乎不會走到（同源、immutable、CDN），而它的代價
      只有在真的失敗時才付 —— 那正是「退路」該有的成本結構。

      英文自己也載不到的話就不再試：再抓一次的來源和剛剛失敗的
      是同一個。畫面上會出現原始的 key，而那比一片空白好：
      它看得出是哪裡壞了。
    */
    if (locale !== "en") {
      try {
        const fallback = await LOADERS.en();
        LOADED.set("en", fallback.default);
      } catch {
        /* 網路整個斷了。畫面會顯示 key —— 那至少看得出是什麼壞了 */
      }
    }
  }
}

/** 語系 → 預設顯示幣別（使用者仍可自行切換） */
/*
  幣別除台灣以外一律 USD。

  這張表原本讓每個語系各對一種幣別（ja→JPY、es→EUR），
  但顯示幣別跟語言無關，跟市場才有關 —— 一個在美國市場
  用日文介面的人，看到的金額應該還是美元。

  而且各國幣別要換算，換算要匯率、匯率會變：同一個案子
  昨天顯示 €950、今天 €960，發案方會以為預算被改過。
*/
/*
  語系對顯示幣別。

  台灣以外一律 USD —— 金額本來就以 USD 的最小單位儲存，
  各國幣別只是顯示層的換算，而換算需要匯率、匯率會變。
  一個案子昨天顯示 €950、今天顯示 €960，發案方會以為預算被改過。

  所以這張表其實只有一個例外（台灣走綠界，實際結帳就是新台幣），
  其餘每一列都是 USD。列出來而不是用預設值，是因為
  Record<Locale, …> 會在新增語系時強迫這裡做一次決定 ——
  而「這個市場用什麼幣別結帳」正是不該被預設值悄悄決定的事。
*/
const STORAGE_LOCALE = "colourdev.locale";
const STORAGE_CURRENCY = "colourdev.currency";

type Vars = Record<string, string | number>;

interface I18nValue {
  locale: Locale;
  /**
   * 在地化的市場名稱（「台灣」而不是 "Taiwan"）。
   *
   * 要把市場名稱直接印在畫面上時用這個，不要用 market.name ——
   * 那一個永遠是英文。
   */
  marketLabel: string;
  setLocale: (next: Locale) => void;
  currency: CurrencyCode;
  setCurrency: (next: CurrencyCode) => void;
  /**
   * 這個市場自己的幣別。金額就是用它存的。
   *
   * 和 currency 不同：currency 是使用者選擇的**顯示**幣別，
   * marketCurrency 是資料的幣別。會員費這種「各市場各自訂價」的
   * 東西要用它去查價，查完再交給 money() 顯示。
   */
  marketCurrency: CurrencyCode;
  /** 翻譯；第二參數用來填 {name} 這類佔位符 */
  t: (key: TranslationKey, vars?: Vars) => string;
  /** 金額格式化，已套用目前語系與幣別 */
  /**
   * 市場幣別的金額 → 使用者選的幣別。
   *
   * 站上絕大多數的金額（案件預算、時薪、合約）都是用市場自己的
   * 幣別存的 —— 台灣的案子存台幣。所以來源幣別不必每次傳。
   */
  money: (amountMinor: number, compact?: boolean) => string;
  moneyRange: (minMinor: number, maxMinor: number, suffix?: string) => string;
  /**
   * 已知幣別的金額（請款單自己帶 currency 欄位）。
   *
   * 和 money 分開而不是加一個可選參數：請款單是一份**已經送出去
   * 的憑證**，它的幣別是資料的一部分，不是市場的屬性。
   * 一個台灣的接案者開一張美元的請款單給國外客戶是合理的。
   */
  moneyIn: (amountMinor: number, from: CurrencyCode, compact?: boolean) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in vars ? String(vars[key]) : match,
  );
}

/** 從瀏覽器語言猜一個支援的語系 */
export function detectLocale(): Locale {
  if (typeof window === "undefined") return defaultLocale;

  const stored = window.localStorage.getItem(STORAGE_LOCALE);
  if (stored && isLocale(stored)) return stored;

  for (const candidate of navigator.languages ?? [navigator.language]) {
    if (isLocale(candidate)) return candidate;
    // zh-Hant-TW / zh-tw → zh-TW；en-GB → en
    const base = candidate.split("-")[0]?.toLowerCase();
    if (base === "zh") return "zh-TW";
    const match = locales.find((l) => l.split("-")[0].toLowerCase() === base);
    if (match) return match;
  }
  return defaultLocale;
}

/**
 * globalVars：每次 t() 都會自動帶入的變數。
 *
 * {market} 出現在十幾條文案裡（「只做 {market} 市場」「比較 {market}
 * 在地專才的提案」）。要求每個呼叫端都記得傳 { market: market.name }
 * 是行不通的 —— 漏一個地方，使用者就會在畫面上看到大括號。
 *
 * 市場是整個 App 的常數（由網址前綴決定，不會在執行中變），
 * 所以在 Provider 這一層注入才是對的位置。
 */
export function I18nProvider({
  locale,
  marketSlug,
  marketCurrency,
  children,
}: {
  locale: Locale;
  /**
   * 市場的網址代碼（tw / jp / us …），不是名稱。
   *
   * 這裡本來收的是 marketName，而 App.tsx 傳的是 market.name ——
   * 那是**英文**的國名。於是十八種語言的站上，頁首徽章寫「Taiwan」、
   * 頁尾寫「目前市場：Taiwan」、Hero 寫「只顯示 Taiwan 的案件」。
   *
   * markets.ts 的註解早就寫著「顯示時走 i18n key market.<slug>」，
   * 但那些 key 從來沒有被建立過。
   *
   * 這件事同時也是 SEO 問題：台灣人搜的是「台灣 接案平台」，
   * 不是「Taiwan 接案平台」。
   *
   * 改收 slug，名稱在這裡從字典查 —— 換語言的時候它會跟著換，
   * 而傳一個已經翻好的名稱進來就不會。
   */
  marketSlug: string;
  /** 這個市場的幣別。金額就是用它存的，也是預設的顯示幣別 */
  marketCurrency: CurrencyCode;
  children: ReactNode;
}) {
  const [currency, setCurrencyState] = useState<CurrencyCode>(() => {
    /*
      預設跟著**市場**，不是語系。

      原本是 LOCALE_CURRENCY[locale] —— 一個在台灣市場用英文
      介面瀏覽的人，會看到台灣的案子以美元計價。而這個站是
      「各國在地的接案系統」：台灣的案子就是台幣計價，
      介面語言換成英文不會改變那件事。

      語系只決定數字的寫法（千分位、符號位置），不決定幣別。
    */
    if (typeof window === "undefined") return marketCurrency;
    const stored = window.localStorage.getItem(STORAGE_CURRENCY);
    if (stored && (CURRENCIES as string[]).includes(stored)) return stored as CurrencyCode;
    return marketCurrency;
  });

  // 語系變動時同步 <html lang> 與 localStorage，讓螢幕閱讀器與斷詞規則正確
  useEffect(() => {
    document.documentElement.lang = locale;
    /*
      書寫方向設在 <html> 而不是某個容器上。

      捲軸的位置、原生表單控制項的對齊、瀏覽器的文字選取行為
      都跟著這個屬性走 —— 設在下層的話那些會留在錯的一邊。

      這讓文字流向正確，但版面沒有完全鏡射（見 config.ts 的
      rtlLocales）。
    */
    document.documentElement.dir = isRtl(locale) ? "rtl" : "ltr";
    window.localStorage.setItem(STORAGE_LOCALE, locale);
  }, [locale]);

  const setCurrency = useCallback((next: CurrencyCode) => {
    setCurrencyState(next);
    window.localStorage.setItem(STORAGE_CURRENCY, next);
  }, []);

  /*
    字典的載入。

    ── 正常情況下這裡不會做任何事 ──────────────────────────

    main.tsx 在掛載之前就呼叫過 preloadDictionary，所以第一次
    render 時 LOADED 裡已經有這個語系了 —— 下面的 useState
    直接拿到正確的字典，不會經過英文。

    ⚠ 這段註解上一版寫著「為什麼要擋住畫面」，但程式碼從來
    沒有擋過 —— 它先用英文渲染再換掉。於是每個非英語使用者的
    第一次繪製都是英文，而語言選單同時顯示著「繁體中文」，
    兩者對不起來。使用者回報的說法是「語言切換反了」。

    一段描述了「沒有實作的設計」的註解，比沒有註解更糟：
    它讓下一個讀的人（包括我自己）以為問題已經被處理了。

    ── 那這裡還留著做什麼 ──────────────────────────────────

    兩種情況會走到：

      切換語言    在站內從中文切到日文，沒有重新載入頁面，
                  所以 main.tsx 不會再跑一次
      預載失敗    網址解析不出語系、或那次 import 失敗

    第二種會退回英文，而那是可以接受的 —— t() 本來就有那條退路。

    ── 為什麼用模組層級的 Map 記住 ─────────────────────────

    切語言再切回來時不該重新解析。瀏覽器對那個 chunk 本來就有
    HTTP 快取，這一層省掉的是重新解析 JS 與一次 Promise 往返。
  */
  const [dictionary, setDictionary] = useState<Partial<Dictionary>>(
    /*
      main.tsx 在掛載之前就 await 過 preloadDictionary，所以這裡
      拿得到正確的字典。

      退路是**英文（如果載過）**，再退是空物件 —— 空物件會讓畫面
      顯示原始的 key，而那看得出是哪裡壞了；以前這裡退回一個
      永遠存在的英文字典，代價是每個人都要先下載它。
    */
    () => LOADED.get(locale) ?? LOADED.get("en") ?? {},
  );

  useEffect(() => {
    const cached = LOADED.get(locale);
    if (cached) {
      setDictionary(cached);
      return;
    }

    let cancelled = false;
    const load = LOADERS[locale];
    if (!load) return;

    load()
      .then((mod) => {
        LOADED.set(locale, mod.default);
        if (!cancelled) setDictionary(mod.default);
      })
      .catch(() => {
        /*
          載入失敗就繼續用英文。

          可能發生的原因：部署換版時舊的 chunk 檔名已經不存在
          （使用者開著舊分頁），或網路斷了。兩種情況下，
          英文介面都比一個當掉的畫面好 —— 而 t() 本來就會
          在查不到 key 時退回英文，所以那條路徑是走得通的。
        */
      });

    return () => {
      cancelled = true;
    };
  }, [locale]);

  const value = useMemo<I18nValue>(() => {
    const dict = dictionary;

    // 呼叫端傳的 vars 優先，讓個別頁面仍然可以覆寫
    /*
      市場名稱從字典查。查不到就退回代碼的大寫 —— 那至少是
      「TW」而不是一個空字串或大括號。
    */
    const marketLabel =
      dict[`market.${marketSlug}` as TranslationKey] ??
      LOADED.get("en")?.[`market.${marketSlug}` as TranslationKey] ??
      marketSlug.toUpperCase();

    const t = (key: TranslationKey, vars?: Vars) =>
      interpolate(dict[key] ?? LOADED.get("en")?.[key] ?? key, {
        market: marketLabel,
        ...vars,
      });

    return {
      locale,
      /** 在地化的市場名稱。要直接印在畫面上時用它，不要用 market.name */
      marketLabel,
      // setLocale 實際的導頁交給 LocaleSwitcher（要改 URL），這裡只留 API 形狀
      setLocale: () => {},
      currency,
      setCurrency,
      marketCurrency,
      t,
      money: (amountMinor, compact = false) =>
        formatMoney(amountMinor, marketCurrency, currency, locale, compact),
      moneyIn: (amountMinor, from, compact = false) =>
        formatMoney(amountMinor, from, currency, locale, compact),
      moneyRange: (minMinor, maxMinor, suffix = "") =>
        formatMoneyRange(minMinor, maxMinor, marketCurrency, currency, locale, suffix),
    };
    /*
      dictionary 必須在依賴裡。

      少了它就是「站內切換語言要按重新整理才生效」的原因：
      locale 變的那一次 render，dictionary 還是舊的（effect 在
      render 之後才跑）。等 setDictionary 把新字典放進來時，
      這個 memo 的依賴一個都沒變 —— 於是 t() 永遠關閉在舊字典上，
      直到整頁重新載入。

      value 每次換語言重算一次，不是效能問題：換語言本來就要
      重畫整棵樹。
    */
  }, [dictionary, locale, marketSlug, marketCurrency, currency, setCurrency]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
  return ctx;
}

/** 只要翻譯函式時的簡寫 */
export function useT() {
  return useI18n().t;
}

export type { TranslationKey };
